import React, { CSSProperties } from 'react';

import { formatCurrency } from '~libs/dataUtils';

const MARKER_WARNING_THRESHOLD = 1.1; // 10% above marker

const formatHours = (value: number): string => {
  const rounded = Math.round(value * 100) / 100;
  return `${rounded}h`;
};

const formatValue = (value: number, units: 'currency' | 'hours' = 'currency'): string => {
  return units === 'hours' ? formatHours(value) : formatCurrency(value);
};

export interface ProgressBarData {
  used: number;
  total: number;
  target?: number;
  units?: 'currency' | 'hours';
}

export interface ProgressBarColours {
  normal?: string;
  warning?: string;
  danger?: string;
  background?: string;
  marker?: string;
  text?: {
    used?: string;
    total?: string;
    title?: string;
    label?: string;
  };
}

export interface ProgressBarProps {
  data: ProgressBarData;
  title?: string;
  label?: string;
  height?: number;
  width?: string;
  showTooltip?: boolean;
  showLabel?: boolean;
  colours?: ProgressBarColours;
  /**
   * Opt-in. Shows use beyond the total in full instead of capping it at the total: the in-bar text and the default
   * label use the real figure, and the fill turns danger red once used is above total. The fill width still stops
   * at 100%. Defaults to false so existing bars (care plan overview) are unchanged.
   */
  overrun?: boolean;
}

const getFillColour = (
  clampedUsed: number,
  target?: number,
  colours?: ProgressBarColours,
): { className: string; style?: CSSProperties } => {
  if (target === undefined) {
    return {
      className: 'progress-bar-container__fill--normal',
      style: colours?.normal ? { backgroundColor: colours.normal } : undefined,
    };
  }

  const warningThreshold = target * MARKER_WARNING_THRESHOLD;

  if (clampedUsed <= target) {
    return {
      className: 'progress-bar-container__fill--normal',
      style: colours?.normal ? { backgroundColor: colours.normal } : undefined,
    };
  } else if (clampedUsed <= warningThreshold) {
    return {
      className: 'progress-bar-container__fill--warning',
      style: colours?.warning ? { backgroundColor: colours.warning } : undefined,
    };
  } else {
    return {
      className: 'progress-bar-container__fill--danger',
      style: colours?.danger ? { backgroundColor: colours.danger } : undefined,
    };
  }
};

const ProgressBar: React.FC<ProgressBarProps> = ({
  data,
  title,
  label,
  height = 50,
  width = '100%',
  showTooltip = true,
  showLabel = false,
  colours,
  overrun = false,
}) => {
  const { used = 0, total = 0, target, units = 'currency' } = data;

  // Safety checks for invalid values
  const safeUsed = isNaN(used) ? 0 : Math.max(0, used);
  const safeTotal = isNaN(total) || total <= 0 ? 0 : Math.max(0, total);

  // Clamp usage at total
  const clampedUsed = Math.min(safeUsed, safeTotal);

  // In overrun mode the figures shown are never capped, only the fill width is
  const isOverrun = overrun && safeUsed > safeTotal;
  const shownUsed = overrun ? safeUsed : clampedUsed;

  // Percentages for fill and marker - handle division by zero
  const ratioOf = (amount: number) => (safeTotal > 0 ? (amount / safeTotal) * 100 : 0);
  const percentage = isOverrun ? 100 : ratioOf(clampedUsed);
  const shownPercentage = ratioOf(shownUsed);
  // An overrun against a zero total fills the bar 100% red, but its ratio is undefined: the default label omits the
  // percentage there ("$50.00 / $0.00") rather than show a contradictory 0%. Every other case keeps "(n%)".
  const showsPercentage = !(isOverrun && safeTotal === 0);
  const percentageText = showsPercentage ? ` (${isNaN(shownPercentage) ? 0 : Math.round(shownPercentage)}%)` : '';
  const rawMarker = target !== undefined && safeTotal > 0 ? (target / safeTotal) * 100 : null;
  const markerPosition = rawMarker !== null ? Math.min(100, Math.max(0, rawMarker)) : null;

  // Determine fill colour
  const fillColour = isOverrun
    ? {
        className: 'progress-bar-container__fill--danger',
        style: colours?.danger ? { backgroundColor: colours.danger } : undefined,
      }
    : getFillColour(clampedUsed, target, colours);

  // Header section
  const renderHeader = () => {
    if (!title && !showLabel) return null;

    return (
      <div className="progress-bar-container__header">
        {title && (
          <span
            className="progress-bar-container__title"
            style={{ ...(colours?.text?.title && { color: colours.text.title }) }}
          >
            {title}
          </span>
        )}

        {showLabel && (
          <span
            className="progress-bar-container__label"
            style={{ ...(colours?.text?.label && { color: colours.text.label }) }}
          >
            {label ||
              `${formatValue(shownUsed, units)} / ${formatValue(safeTotal, units)}${percentageText}`}
          </span>
        )}
      </div>
    );
  };

  // Tooltip text
  const getTooltipText = (): string | undefined => {
    if (!showTooltip || target === undefined) return undefined;

    const targetPercentage = safeTotal > 0 ? Math.round((target / safeTotal) * 100) : 0;
    return `Expected: ${formatValue(target, units)} (${isNaN(targetPercentage) ? 0 : targetPercentage}%)`;
  };

  return (
    <div className="progress-bar-container" style={{ width }}>
      {renderHeader()}

      <div className="progress-bar-container__wrapper">
        <div
          className="progress-bar-container__track"
          style={{
            height: `${height}px`,
            ...(colours?.background && { backgroundColor: colours.background }),
          }}
          title={getTooltipText()}
        >
          {/* Progress fill */}
          <div
            className={`progress-bar-container__fill ${fillColour.className}`}
            style={{ width: `${percentage}%`, ...fillColour.style }}
          />

          {/* Text overlays */}
          {height > 20 && percentage > 0 && (
            <div
              className="progress-bar-container__text progress-bar-container__text--used"
              style={{
                left: `${percentage / 2}%`,
                ...(colours?.text?.used && { color: colours.text.used }),
              }}
            >
              {formatValue(shownUsed, units)}
            </div>
          )}

          {height > 20 && (
            <div
              className={`progress-bar-container__text progress-bar-container__text--total${
                isOverrun ? ' progress-bar-container__text--on-fill' : ''
              }`}
              style={{
                ...(colours?.text?.total && { color: colours.text.total }),
              }}
            >
              {formatValue(safeTotal, units)}
            </div>
          )}
        </div>

        {/* Target marker */}
        {markerPosition !== null && (
          <div
            className="progress-bar-container__marker"
            style={{
              left: `${markerPosition}%`,
              height: `${height + 12}px`,
              ...(colours?.marker && { backgroundColor: colours.marker }),
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ProgressBar;
